/**
 * Day/night switch — the icon button in the header, and the three-state
 * segmented control (Hell · Dunkel · System) inside the user menu.
 *
 * The icon button intentionally exposes only two states: it shows what a click
 * WILL DO (a moon when it would go dark), because a control that renders the
 * current state is read backwards by half of all users. 'system' is reachable
 * from the segmented control only — it is a setting, not a click target.
 */

import type { ReactNode } from 'react';

import { useTheme, type ThemePreference } from '../lib/theme';

/** Feather-style icons, inline so no request is needed and both follow currentColor. */
function SunIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
      strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2.6v2.2M12 19.2v2.2M2.6 12h2.2M19.2 12h2.2M5.4 5.4l1.6 1.6M17 17l1.6 1.6M18.6 5.4L17 7M7 17l-1.6 1.6" />
    </svg>
  );
}

function MoonIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
      strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20.5 14.6A8.6 8.6 0 0 1 9.4 3.5a8.8 8.8 0 1 0 11.1 11.1z" />
    </svg>
  );
}

function MonitorIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
      strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2.8" y="4" width="18.4" height="12" rx="2" />
      <path d="M8.5 20h7M12 16v4" />
    </svg>
  );
}

/** Single icon button: flips the visible palette. */
export function ThemeToggle(): ReactNode {
  const { theme, toggle } = useTheme();
  const goingDark = theme === 'light';
  const label = goingDark ? 'Dunkles Design aktivieren' : 'Helles Design aktivieren';

  return (
    <button
      type="button"
      className="icon-button theme-toggle"
      onClick={toggle}
      title={label}
      aria-label={label}
    >
      {goingDark ? <MoonIcon /> : <SunIcon />}
    </button>
  );
}

const OPTIONS: readonly { value: ThemePreference; label: string; icon: () => ReactNode }[] = [
  { value: 'light', label: 'Hell', icon: SunIcon },
  { value: 'dark', label: 'Dunkel', icon: MoonIcon },
  { value: 'system', label: 'System', icon: MonitorIcon },
];

/**
 * Three-state control. A radiogroup rather than three buttons, so arrow keys
 * work and a screen reader announces which of the three is selected.
 */
export function ThemePreferenceControl(): ReactNode {
  const { preference, setPreference } = useTheme();

  return (
    <div className="theme-segmented" role="radiogroup" aria-label="Design">
      {OPTIONS.map((option) => {
        const Icon = option.icon;
        const active = preference === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            className={active ? 'active' : undefined}
            onClick={() => setPreference(option.value)}
          >
            <Icon />
            <span>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default ThemeToggle;
